/**
 * Device Detection Utilities
 * Detect device type, browser, and platform information
 */

import type { DeviceType } from '../types'

/** Breakpoints matching CLAUDE.md responsive design specs */
const BREAKPOINTS = {
  xs: 475,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

/**
 * Get device type based on viewport width
 */
export function getDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'desktop'

  const width = window.innerWidth

  if (width < BREAKPOINTS.sm) return 'mobile'
  if (width < BREAKPOINTS.lg) return 'tablet'
  return 'desktop'
}

/**
 * Check if device is touch-capable
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  )
}

/**
 * Check if RTL mode is active
 */
export function isRtl(): boolean {
  if (typeof document === 'undefined') return false

  return document.documentElement.dir === 'rtl'
}

/**
 * Get current locale
 */
export function getLocale(): string {
  if (typeof document === 'undefined') return 'en'

  return document.documentElement.lang || 'en'
}

/**
 * Get browser name
 */
export function getBrowserName(): string {
  if (typeof navigator === 'undefined') return 'unknown'

  const ua = navigator.userAgent

  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser'
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera'
  if (ua.includes('Trident')) return 'Internet Explorer'
  if (ua.includes('Edge')) return 'Edge Legacy'
  if (ua.includes('Edg')) return 'Edge'
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Safari')) return 'Safari'

  return 'unknown'
}

/**
 * Get operating system
 */
export function getOS(): string {
  if (typeof navigator === 'undefined') return 'unknown'

  const ua = navigator.userAgent

  if (ua.includes('Win')) return 'Windows'
  if (ua.includes('Mac')) return 'macOS'
  if (ua.includes('Linux')) return 'Linux'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'

  return 'unknown'
}

/**
 * Get connection type (if available)
 */
export function getConnectionType(): string | undefined {
  if (typeof navigator === 'undefined') return undefined

  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string }
  }

  return nav.connection?.effectiveType
}

/**
 * Get viewport dimensions
 */
export function getViewportDimensions(): { width: number; height: number } {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 }
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

/**
 * Get screen dimensions
 */
export function getScreenDimensions(): { width: number; height: number } {
  if (typeof screen === 'undefined') {
    return { width: 0, height: 0 }
  }

  return {
    width: screen.width,
    height: screen.height,
  }
}
